import { StanCard, StanFilters, StanTable } from '@/components';
import { useQueryFilters } from '@/hooks/useQueryFilters';
import { useStanRouter } from '@/hooks/useStanRouter';
import { useMyStore } from '@/queries/useStoreData';
import { useEffect, useEffectEvent, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CUSTOMERS_PAGE_SIZE, EMPTY_STATE } from '../constants/customers.constants';
import { useCustomerFilters } from '../hooks/useCustomerFilters';
import { useCustomerModals } from '../hooks/useCustomerModals';
import { useCustomersColumns } from '../hooks/useCustomersColumns';
import { useCustomers } from '../queries';
import type { Customer } from '../types/customers.types';
import { CustomerActionsDropdown } from './CustomerActionsDropdown';
import { CustomerModals } from './CustomerModals';
import { CustomersEmptyState } from './CustomersEmptyState';

export function CustomersTable() {
    const { push } = useStanRouter();
    const [searchParams, setSearchParams] = useSearchParams();
    const customerFilters = useCustomerFilters();
    const { activeModal, handleActionSelect, handleModalClose } = useCustomerModals();

    // Get current page from URL, default to 1
    const currentPage = parseInt(searchParams.get('page') || '1', 10);

    // Validate page number
    const validatedPage = isNaN(currentPage) || currentPage < 1 ? 1 : currentPage;

    const { filterValues, backendFilterValues, handleFilterChange, handleResetFilters } = useQueryFilters({
        filters: customerFilters,
    });

    const { data, isLoading } = useCustomers({
        page: validatedPage,
        pageSize: CUSTOMERS_PAGE_SIZE,
        filter_array: backendFilterValues,
    });
    // Extract data from query response (must be before effects that use these values)
    const customers = data?.data ?? [];
    const totalPages = data?.metadata?.totalPages ?? 1;
    const totalCustomers = data?.metadata?.totalCustomers ?? 0;

    // Check if store has affiliate products
    const { data: stores } = useMyStore();
    const hasAffiliateProduct = useMemo(() => {
        if (!stores || stores.length === 0) return false;
        // return stores.some(store =>
        //     store.pages?.some(page => page.data?.resell_rights?.enabled && page.data?.resell_rights?.percentage > 0)
        // );
        return true;
    }, [stores]);

    const handleRowClick = (customer: Customer) => {
        push(`/audience/details/${customer.userFanId}`);
    };

    const handlePageChange = (page: number) => {
        setSearchParams(prev => {
            prev.set('page', page.toString());
            return prev;
        });
    };

    // Reset to page 1 when filters change (but not when page changes)
    const resetToFirstPage = useEffectEvent(() => {
        setSearchParams(prev => {
            prev.set('page', '1');
            return prev;
        });
    });

    // Create a stable string representation of filter values for comparison
    const filterValuesKey = useMemo(() => {
        return JSON.stringify(
            filterValues.map(f => ({ id: f.id, operator: f.operator, value: f.value, secondValue: f.secondValue }))
        );
    }, [filterValues]);

    const prevFilterValuesKeyRef = useRef<string>('');

    useEffect(() => {
        // Only reset if filters actually changed (not just page)
        if (filterValuesKey !== prevFilterValuesKeyRef.current && filterValues.length > 0 && validatedPage > 1) {
            resetToFirstPage();
        }
        prevFilterValuesKeyRef.current = filterValuesKey;
    }, [filterValuesKey, filterValues.length, validatedPage, resetToFirstPage]);

    // Reset to page 1 if current page has no data (e.g., after bulk delete)
    useEffect(() => {
        if (!isLoading && customers.length === 0 && validatedPage > 1) {
            resetToFirstPage();
        }
    }, [isLoading, customers.length, validatedPage, resetToFirstPage]);

    const columns = useCustomersColumns({
        onRowClick: handleRowClick,
    });

    const totalRowsLabel = totalCustomers > 0 ? `${totalCustomers} Customer${totalCustomers !== 1 ? 's' : ''}` : '';

    // Determine if we should show the custom empty state (no customers and no filters applied)
    const showCustomEmptyState = customers.length === 0 && filterValues.length === 0 && !isLoading;

    return (
        <>
            <StanCard>
                {/* Always show filters */}
                <div className='mb-6 flex w-full items-start justify-between gap-4'>
                    <div className='flex-1'>
                        <StanFilters
                            filters={customerFilters}
                            filterValues={filterValues}
                            onFilterChanged={handleFilterChange}
                            onResetFilters={handleResetFilters}
                        />
                    </div>
                    {customers.length > 0 && (
                        <div className='flex-shrink-0 pt-1'>
                            <CustomerActionsDropdown
                                onActionSelect={handleActionSelect}
                                hasAffiliateProduct={hasAffiliateProduct}
                            />
                        </div>
                    )}
                </div>

                {showCustomEmptyState ? (
                    <CustomersEmptyState />
                ) : (
                    <StanTable
                        columns={columns}
                        data={customers}
                        isLoading={isLoading}
                        page={validatedPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        onRowClick={handleRowClick}
                        totalRowsLabel={totalRowsLabel}
                        emptyHeader={EMPTY_STATE.NO_MATCH.HEADER}
                        emptySubheader={EMPTY_STATE.NO_MATCH.SUBHEADER}
                    />
                )}
            </StanCard>

            <CustomerModals
                activeModal={activeModal}
                onClose={handleModalClose}
                totalCustomers={totalCustomers}
                totalPages={totalPages}
                filterArray={backendFilterValues}
                onDeleteSuccess={handleResetFilters}
            />
        </>
    );
}
